/**
 * useAuth Hook for Re:Nav-96
 *
 * Custom hook for accessing authentication context.
 * Provides type-safe access to auth state and methods.
 *
 * @example
 * import { useAuth } from '@/hooks/useAuth';
 *
 * function ProfileButton() {
 *   const { user, isAuthenticated, logout } = useAuth();
 *
 *   if (!isAuthenticated) {
 *     return <LoginButton />;
 *   }
 *
 *   return (
 *     <div>
 *       <span>Welcome, {user?.displayName}</span>
 *       <button onClick={logout}>Logout</button>
 *     </div>
 *   );
 * }
 */

import { useContext } from 'react';
import { AuthContext, type AuthContextValue } from '@/contexts/AuthContext';

/**
 * Hook to access authentication context
 *
 * Must be used within an AuthProvider.
 * Throws an error if used outside of AuthProvider.
 *
 * @returns The authentication context value with user state and auth methods
 * @throws Error if used outside of AuthProvider
 *
 * @example
 * // Basic usage
 * const { user, isAuthenticated } = useAuth();
 *
 * @example
 * // Login flow
 * const { login, error, isLoading } = useAuth();
 *
 * const handleLogin = async () => {
 *   try {
 *     await login({ email, password });
 *     navigate('/member/dashboard');
 *   } catch {
 *     // Error is available via the `error` state
 *   }
 * };
 *
 * @example
 * // Protected route check
 * const { isAuthenticated, isLoading } = useAuth();
 *
 * if (isLoading) {
 *   return <LoadingSpinner />;
 * }
 *
 * if (!isAuthenticated) {
 *   return <Navigate to="/login" />;
 * }
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error(
      'useAuth must be used within an AuthProvider. ' +
        'Make sure your component is wrapped in <AuthProvider>.'
    );
  }

  return context;
}

/**
 * Hook to check if user has a specific role
 *
 * @param requiredRole - The role to check for
 * @returns True if the user has the required role or higher
 *
 * @example
 * const isAdmin = useHasRole('super_admin');
 * const isGroupAdmin = useHasRole('group_admin');
 */
export function useHasRole(
  requiredRole: 'member' | 'group_admin' | 'super_admin'
): boolean {
  const { user, isAuthenticated } = useAuth();

  if (!isAuthenticated || !user) {
    return false;
  }

  const roleHierarchy = {
    member: 1,
    group_admin: 2,
    super_admin: 3,
  };

  return roleHierarchy[user.role] >= roleHierarchy[requiredRole];
}

/**
 * Hook to check if user is a super admin
 *
 * @returns True if the user is a super admin
 *
 * @example
 * const isSuperAdmin = useIsSuperAdmin();
 * if (isSuperAdmin) {
 *   // Show admin controls
 * }
 */
export function useIsSuperAdmin(): boolean {
  return useHasRole('super_admin');
}

/**
 * Hook to check if user is at least a group admin
 *
 * @returns True if the user is a group admin or super admin
 *
 * @example
 * const canModerate = useIsGroupAdmin();
 */
export function useIsGroupAdmin(): boolean {
  return useHasRole('group_admin');
}

export default useAuth;
